"use client";
import { createContext, useContext, useState, useCallback } from "react";

interface AuthError {
    timestamp: number;
    message: string;
}

interface AuthErrorContextType {
    authError: AuthError | null;
    setAuthError: (error: AuthError | null) => void;
    clearAuthError: () => void;
}

const AuthErrorContext = createContext<AuthErrorContextType | undefined>(undefined);

export function AuthErrorProvider({ children }: { children: React.ReactNode }) {
    const [authError, setAuthError] = useState<AuthError | null>(null);

    const clearAuthError = useCallback(() => {
        setAuthError(null);
    }, []);

    // Override setAuthError to add timestamp for tracking
    const handleSetAuthError = useCallback((error: AuthError | null) => {
        if (error) {
            setAuthError({
                ...error,
                timestamp: Date.now(),
            });
        } else {
            setAuthError(null);
        }
    }, []);

    return (
        <AuthErrorContext.Provider
            value={{
                authError,
                setAuthError: handleSetAuthError,
                clearAuthError,
            }}
        >
            {children}
        </AuthErrorContext.Provider>
    );
}

export function useAuthError() {
    const context = useContext(AuthErrorContext);
    if (!context) {
        throw new Error("useAuthError must be used within AuthErrorProvider");
    }
    return context;
}
